/* eslint-disable @nx/enforce-module-boundaries */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateAdapter, MatOptionModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { distinctUntilChanged, filter } from 'rxjs';

import type { MeetingLanguage } from '@ninety/platform/models/language/custom-language';
import { StateService } from '@ninety/platform/services';
import {
  TerraDateInputModule,
  TerraDividerModule,
  TerraFormFieldModule,
  TerraIconModule,
  TerraRadioButtonModule,
  TerraSelectModule,
} from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';
import {
  CalendarEvent,
  CalendarEventsService,
  ChooseCalendarProviderDialogComponent,
  GoogleCalendarService,
  isOutlookCalendarProvider,
  NativeIntegration,
  NativeIntegrationCoreService,
  OutlookCalendarService,
} from '@ninety/web/pages/shared/integrations';

import { Cadence, MeetingScheduleResponse, CadenceFormOptions, UpdateMeetingScheduleModel } from '../../../_models';
import { MeetingBotCalendarSectionComponent } from '../../../_shared/meeting-bot-calendar-section/meeting-bot-calendar-section.component';
import { SelectCalendarEventDialogComponent } from '../../start-meeting-dialog/select-calendar-event-dialog/select-calendar-event-dialog.component';
import { TimeOption, toTimeOptions, withExtraTimeOption } from '../schedule-meeting-dialog/schedule-meeting-time.logic';

type MonthlyCadenceOption = {
  name: string;
  value: CadenceFormOptions.monthly | CadenceFormOptions.monthlyLastWeek;
};

type UpdateMeetingScheduleForm = {
  date: FormControl<Date | null>;
  time: FormControl<string | null>;
  presenterId: FormControl<string | null>;
  scribeId: FormControl<string | null>;
  updateAll?: FormControl<boolean | null>;
  monthlyCadenceOption?: FormControl<MonthlyCadenceOption['value']>;
};

export type UpdateDialogOptions = {
  scheduleId: string;
  isRepeating: boolean;
  updateAll: boolean;
  update: UpdateMeetingScheduleModel;
};

export type UpdateMeetingScheduleDialogData = {
  meetingLanguage: MeetingLanguage;
  schedule: MeetingScheduleResponse;
  users: { value: string; name: string }[];
};

@Component({
  selector: 'ninety-update-meeting-schedule-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    FormsModule,
    ButtonComponent,
    MatOptionModule,
    TerraIconModule,
    TerraDividerModule,
    TerraRadioButtonModule,
    TerraFormFieldModule,
    TerraSelectModule,
    TerraDateInputModule,
    SelectCalendarEventDialogComponent,
    MeetingBotCalendarSectionComponent,
    ChooseCalendarProviderDialogComponent,
  ],
  templateUrl: './update-meeting-schedule-dialog.component.html',
  styleUrls: ['./update-meeting-schedule-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateMeetingScheduleDialogComponent implements OnInit {
  form: FormGroup<UpdateMeetingScheduleForm>;
  Cadence = Cadence;
  CadenceFormOptions = CadenceFormOptions;
  minDate = new Date();
  maxDate?: Date;
  monthlyCadenceOptions: MonthlyCadenceOption[] = [];

  subtitle: string;

  readonly dialogRef = inject(MatDialogRef<UpdateMeetingScheduleDialogComponent, UpdateDialogOptions>);
  readonly data = inject<UpdateMeetingScheduleDialogData>(MAT_DIALOG_DATA);
  readonly stateService = inject(StateService);
  readonly destroyRef = inject(DestroyRef);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly dateAdapter = inject(DateAdapter<any>);
  private readonly nativeIntegrationService = inject(NativeIntegrationCoreService);
  private readonly googleCalendarService = inject(GoogleCalendarService);
  private readonly outlookCalendarService = inject(OutlookCalendarService);

  readonly pickedEvent = signal<CalendarEvent | undefined>(undefined);

  // Only needed here to resolve the connectionId for the swapped event picker below - the rest of
  // the calendar-connect UI/state lives in MeetingBotCalendarSectionComponent.
  readonly activeCalendarConnectionId = computed(() => this.nativeIntegrationService.activeCalendarConnectionId());

  readonly calendarEventsService = computed<CalendarEventsService>(() => {
    const integration = this.nativeIntegrationService.activeCalendarIntegration();
    return integration && isOutlookCalendarProvider(integration)
      ? this.outlookCalendarService
      : this.googleCalendarService;
  });

  private readonly _timeOptions = signal<readonly TimeOption[]>(toTimeOptions());
  readonly timeOptions = this._timeOptions.asReadonly();

  // Existing schedules only persisted a raw meeting link, not the event's title/date. Look up the
  // matching calendar event once a connection is available so we can show a proper title+date
  // card instead of the raw link - falls back to the raw link if no match is found.
  private eventLookupStarted = false;
  readonly resolvedExistingEvent = computed(() => {
    const meetingLink = this.data.schedule.meetingLink;
    if (!meetingLink) return undefined;
    return this.calendarEventsService()
      .events()
      .find(event => event.meetingUrl === meetingLink);
  });

  // Defaults on when the schedule already has a meeting link (e.g. MeetingAgent was previously enabled
  // for it), so editing an existing linked schedule doesn't hide it behind an off toggle.
  readonly addMeetingAgentControl = new FormControl(false);

  // Swaps this dialog's own content for the calendar picker in place, rather than opening it as a
  // nested modal on top.
  readonly showCalendarPicker = signal(false);

  // Same swap-in-place pattern for the choose-provider step, so the meeting dialog stays in the
  // background instead of a second dialog stacking on top of it.
  readonly showProviderChooser = signal(false);
  readonly providerChooserIntegrations = signal<NativeIntegration[]>([]);

  constructor() {
    this.nativeIntegrationService.fetchNativeIntegrations();

    // Updates locale for date input (temp)
    this.stateService.localeChange$.pipe(takeUntilDestroyed()).subscribe({
      next: l => {
        this.dateAdapter.setLocale(l);
      },
    });

    effect(() => {
      const connectionId = this.activeCalendarConnectionId();
      if (!connectionId || this.eventLookupStarted || !this.data.schedule.meetingLink) {
        return;
      }
      this.eventLookupStarted = true;

      const scheduledDate = new Date(this.data.schedule.scheduledDate);
      this.calendarEventsService().getEvents(
        connectionId,
        startOfDay(subDays(scheduledDate, 1)).toISOString(),
        endOfDay(addDays(scheduledDate, 1)).toISOString()
      );
    });
  }

  ngOnInit(): void {
    const scheduledDate = new Date(this.data.schedule.scheduledDate);
    const dateBounds = this.generateDateBounds(scheduledDate);
    this.minDate = dateBounds.minDate;
    this.maxDate = dateBounds.maxDate;

    this.subtitle = this.generateSubtitle();
    this.addMeetingAgentControl.setValue(!!this.data.schedule.meetingLink);

    // addMeetingAgentControl lives outside `form` (shared with the calendar-section component), so
    // toggling it wouldn't otherwise mark `form` dirty and enable Save.
    this.addMeetingAgentControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.form.markAsDirty();
    });

    const initialTime = format(scheduledDate, 'HH:mm');
    this._timeOptions.set(withExtraTimeOption(toTimeOptions(), initialTime));

    const { presenterId, scribeId } = this.data.schedule;
    this.form = new FormGroup<UpdateMeetingScheduleForm>({
      date: new FormControl(scheduledDate, Validators.required),
      time: new FormControl(initialTime, Validators.required),
      presenterId: new FormControl(presenterId, Validators.required),
      scribeId: new FormControl(scribeId),
    });

    if (this.data.schedule.cadence !== Cadence.none) {
      this.form.addControl('updateAll', new FormControl<boolean | null>(null, Validators.required));
    }

    if (this.data.schedule.cadence === Cadence.monthly) {
      this.monthlyCadenceOptions = this.getMonthlyCadenceOptions(scheduledDate);
      const defaultOption = this.getDefaultMonthlyCadenceOption(
        this.monthlyCadenceOptions,
        this.data.schedule.lastWeekOfMonth
      );

      this.form.addControl(
        'monthlyCadenceOption',
        new FormControl<MonthlyCadenceOption['value']>(defaultOption.value, Validators.required)
      );
      this.watchMonthlyDateChanges();
    }
  }

  openEventPicker(): void {
    if (!this.activeCalendarConnectionId()) {
      return;
    }
    this.showCalendarPicker.set(true);
  }

  onCalendarClosed(event: CalendarEvent | undefined): void {
    this.showCalendarPicker.set(false);
    if (event) {
      this.pickedEvent.set(event);
      this.form.markAsDirty();
    }
  }

  onOpenProviderChooser(integrations: NativeIntegration[]): void {
    this.providerChooserIntegrations.set(integrations);
    this.showProviderChooser.set(true);
  }

  onProviderChooserClosed(): void {
    this.showProviderChooser.set(false);
  }

  onUpdate() {
    const { date, time, presenterId, scribeId, updateAll, monthlyCadenceOption } = this.form.getRawValue();

    const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const [hours, minutes] = time.split(':').map(Number);

    const scheduledDate = new Date(dateWithoutTime.setHours(hours, minutes));
    const updatePayload: UpdateMeetingScheduleModel = {
      scheduledDate,
      presenterId,
      scribeId: scribeId || null,
      meetingLink: this.addMeetingAgentControl.value
        ? (this.pickedEvent()?.meetingUrl ?? this.data.schedule.meetingLink ?? null)
        : null,
    };

    if (this.data.schedule.cadence === Cadence.monthly && updateAll) {
      // Preserve whether the monthly series should recur on the last matching weekday.
      updatePayload.lastWeekOfMonth = monthlyCadenceOption === CadenceFormOptions.monthlyLastWeek;
    }

    const result: UpdateDialogOptions = {
      updateAll: !!updateAll,
      //when updating all send the parent id if it exists, otherwise it is already a parent id
      //for update one instance we send the schedule id which is the instance being edited
      scheduleId: !!updateAll ? this.data.schedule.parentId || this.data.schedule._id : this.data.schedule._id,
      isRepeating: this.data.schedule.cadence !== Cadence.none,
      update: updatePayload,
    };

    this.dialogRef.close(result);
  }

  generateSubtitle() {
    const schedule = this.data.schedule;

    let text = '';

    if (schedule.cadence === Cadence.none) {
      text = `This ${this.data.meetingLanguage.item} is not repeating.`;
    }

    if (schedule.cadence === Cadence.weekly) {
      text = `This ${this.data.meetingLanguage.item} is repeating weekly on ${this.getDayOfTheWeek(
        schedule.cadenceStartDate
      )}.`;
    }

    if (schedule.cadence === Cadence.biWeekly) {
      text = `This ${this.data.meetingLanguage.item} is repeating every 2 weeks on ${this.getDayOfTheWeek(
        schedule.cadenceStartDate
      )}.`;
    }

    if (schedule.cadence === Cadence.monthly) {
      if (schedule.lastWeekOfMonth) {
        text = `This ${this.data.meetingLanguage.item} is repeating monthly on the last ${this.getDayOfTheWeek(
          schedule.cadenceStartDate
        )} of the month.`;
      } else {
        text = `This ${this.data.meetingLanguage.item} is repeating monthly on the ${this.getNthDayOfTheMonth(
          schedule.cadenceStartDate
        )} ${this.getDayOfTheWeek(schedule.cadenceStartDate)}.`;
      }
    }

    if (schedule.parentId) {
      text += ' This instance was updated from the original.';
    }

    return text;
  }

  private getNthDayOfTheMonth(date: string): string {
    const dayNum = parseISO(date).getDate();
    const ordinals = ['first', 'second', 'third', 'fourth', 'fifth'];
    return ordinals[Math.floor((dayNum - 1) / 7)];
  }

  private getDayOfTheWeek(date: string): string {
    return format(parseISO(date), 'EEEE');
  }

  private getMonthlyCadenceOptions(date: Date): MonthlyCadenceOption[] {
    const dayOfWeek = format(date, 'EEEE');
    const dayNum = date.getDate();
    // Convert calendar day to occurrence index within the month (1..5).
    const nthWeekOfTheMonth = Math.floor((dayNum - 1) / 7) + 1;

    // In update flow, a fourth occurrence can map to either "fourth" or "last".
    if (nthWeekOfTheMonth === 4) {
      return [
        { name: `Monthly on the fourth ${dayOfWeek}`, value: CadenceFormOptions.monthly },
        { name: `Monthly on the last ${dayOfWeek}`, value: CadenceFormOptions.monthlyLastWeek },
      ];
    }

    // A fifth occurrence is always the last one in the month.
    if (nthWeekOfTheMonth === 5) {
      return [{ name: `Monthly on the last ${dayOfWeek}`, value: CadenceFormOptions.monthlyLastWeek }];
    }

    // For weeks 1..3, only the explicit nth weekday option applies.
    const ordinals = ['first', 'second', 'third'];
    return [
      {
        name: `Monthly on the ${ordinals[Math.floor((dayNum - 1) / 7)]} ${dayOfWeek}`,
        value: CadenceFormOptions.monthly,
      },
    ];
  }

  private getDefaultMonthlyCadenceOption(
    options: MonthlyCadenceOption[],
    isLastWeekOfMonth?: boolean
  ): MonthlyCadenceOption {
    // Keep existing "last week" cadence selected when available; otherwise use first option.
    return isLastWeekOfMonth
      ? (options.find(option => option.value === CadenceFormOptions.monthlyLastWeek) ?? options[0])
      : options[0];
  }

  private refreshMonthlyCadenceOptions(date: Date): void {
    // Recompute available cadence options for the newly selected date.
    this.monthlyCadenceOptions = this.getMonthlyCadenceOptions(date);
    const control = this.form.controls.monthlyCadenceOption;

    // Keep current selection only if it is still valid for the new options.
    const nextValue = this.monthlyCadenceOptions.some(option => option.value === control.value)
      ? control.value
      : this.monthlyCadenceOptions[0].value;

    // Apply the resolved selection to keep form state in sync with options.
    control.setValue(nextValue);
  }

  private watchMonthlyDateChanges(): void {
    // Keep monthly cadence dropdown options aligned with the selected date.
    // Without this, options/defaults can become stale after the user changes the date.
    this.form.controls.date.valueChanges
      .pipe(
        filter((date): date is Date => date !== null),
        distinctUntilChanged((a, b) => a?.getTime() === b?.getTime()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(date => this.refreshMonthlyCadenceOptions(date));
  }

  private generateDateBounds(scheduledDate: Date): { minDate: Date; maxDate?: Date } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (this.data.schedule.cadence === Cadence.none) {
      return { minDate: today };
    }

    const scheduledDateOnly = startOfDay(new Date(scheduledDate));

    if (this.data.schedule.cadence === Cadence.weekly) {
      // Weekly edits are constrained to the schedule's calendar week.
      const weekStart = startOfWeek(scheduledDateOnly);
      const weekEnd = startOfDay(endOfWeek(scheduledDateOnly));
      // If today falls in that same week, don't allow selecting earlier days.
      const weekIncludesToday = isWithinInterval(today, { start: weekStart, end: weekEnd });
      const minDate = weekIncludesToday ? today : weekStart;

      return { minDate, maxDate: weekEnd };
    }

    if (this.data.schedule.cadence === Cadence.biWeekly) {
      // Bi-weekly edits are constrained to 14-day windows anchored from the week of cadenceStartDate.
      const cadenceStartDateOnly = startOfDay(parseISO(this.data.schedule.cadenceStartDate));
      const cadenceWindowAnchor = startOfWeek(cadenceStartDateOnly);
      const BI_WEEKLY_WINDOW_DAYS = 14;
      const daysFromAnchor = differenceInCalendarDays(scheduledDateOnly, cadenceWindowAnchor);
      const dayOffsetInWindow =
        ((daysFromAnchor % BI_WEEKLY_WINDOW_DAYS) + BI_WEEKLY_WINDOW_DAYS) % BI_WEEKLY_WINDOW_DAYS;
      const windowStart = startOfDay(subDays(scheduledDateOnly, dayOffsetInWindow));
      const windowEnd = startOfDay(addDays(windowStart, BI_WEEKLY_WINDOW_DAYS - 1));
      const windowIncludesToday = isWithinInterval(today, { start: windowStart, end: windowEnd });
      const minDate = windowIncludesToday ? today : windowStart;

      return { minDate, maxDate: windowEnd };
    }

    if (this.data.schedule.cadence === Cadence.monthly) {
      // Monthly edits are constrained to the schedule's calendar month.
      const monthStart = startOfDay(startOfMonth(scheduledDateOnly));
      const monthEnd = startOfDay(endOfMonth(scheduledDateOnly));
      const monthIncludesToday = isWithinInterval(today, { start: monthStart, end: monthEnd });
      const minDate = monthIncludesToday ? today : monthStart;

      return { minDate, maxDate: monthEnd };
    }

    return { minDate: scheduledDateOnly };
  }
}
